import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Plus, Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { API } from './constants';

const EMPTY_FORM = {
  client_id: '',
  title: '',
  event_type: '',
  event_date: '',
  status: 'planning',
  description: '',
  budget: '',
  venue: '',
};

export const EventsTab = ({ events, clients, showModal, setShowModal, editingItem, setEditingItem, fetchAllData }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
      };

      if (editingItem) {
        await axios.put(`${API}/events/${editingItem.event_id}`, data, { withCredentials: true });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, data, { withCredentials: true });
        toast.success('Event created');
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData(EMPTY_FORM);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const openEditModal = (event) => {
    setEditingItem(event);
    setFormData({
      client_id: event.client_id,
      title: event.title,
      event_type: event.event_type,
      event_date: event.event_date,
      status: event.status,
      description: event.description || '',
      budget: event.budget?.toString() || '',
      venue: event.venue || '',
    });
    setShowModal(true);
  };

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl text-white">Events</h2>
        <button
          onClick={() => {
            setEditingItem(null);
            setFormData(EMPTY_FORM);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-6 py-3 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
          data-testid="add-event-btn"
        >
          <Plus size={16} /> Add Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
          <Calendar className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
          <p className="text-white/60">No events yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => {
            const client = clients.find(c => c.user_id === event.client_id);
            return (
              <div
                key={event.event_id}
                className="bg-[#0a0a0a] border border-white/10 p-6 flex items-center justify-between"
                data-testid={`event-${event.event_id}`}
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-medium">{event.title}</h3>
                    <span className={`text-xs uppercase px-2 py-1 ${
                      event.status === 'planning' ? 'bg-blue-50 text-blue-600' :
                      event.status === 'confirmed' ? 'bg-green-50 text-green-600' :
                      event.status === 'completed' ? 'bg-[#B8AEA1]/20 text-white/60' :
                      'bg-[#D4AF37]/20 text-[#D4AF37]'
                    }`}>
                      {event.status}
                    </span>
                  </div>
                  <p className="text-white/60 text-sm">
                    {event.event_type} • {event.event_date} {event.venue && `• ${event.venue}`}
                  </p>
                  {client && (
                    <p className="text-white/40 text-xs mt-1">Client: {client.name}</p>
                  )}
                </div>
                <button
                  onClick={() => openEditModal(event)}
                  className="p-2 text-white/40 hover:text-white"
                >
                  <Edit size={18} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Event Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) setEditingItem(null);
      }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              {editingItem ? 'Edit Event' : 'Add New Event'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Client</label>
              <Select
                value={formData.client_id}
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:ring-0 focus:border-[#D4AF37] text-white h-auto">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {clients.map((client) => (
                    <SelectItem key={client.user_id} value={client.user_id} className="text-white/70 focus:bg-[#050505] focus:text-white">
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="e.g., Johnson Wedding"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Type</label>
                <input
                  type="text"
                  required
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="Wedding, Corporate, etc."
                />
              </div>
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Date</label>
                <input
                  type="date"
                  required
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:ring-0 focus:border-[#D4AF37] text-white h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    {['planning', 'confirmed', 'in-progress', 'completed'].map((status) => (
                      <SelectItem key={status} value={status} className="text-white/70 focus:bg-[#050505] focus:text-white">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Budget</label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="$0.00"
                />
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Venue</label>
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Venue name or location"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-transparent border border-white/20 p-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors min-h-[100px] resize-none"
                placeholder="Event details..."
              />
            </div>
            <button type="submit" className="w-full py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors">
              {editingItem ? 'Update Event' : 'Create Event'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
